import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { LanguageCode } from '~/shared/internationalization';

type JobArchitectureContentLanguageContextValue = {
    readonly contentLanguage: LanguageCode;
    readonly setContentLanguage: (language: LanguageCode) => void;
};

const JobArchitectureContentLanguageContext = createContext<JobArchitectureContentLanguageContextValue | null>(null);

type JobArchitectureContentLanguageProviderProps = PropsWithChildren<{
    readonly initialContentLanguage?: LanguageCode;
}>;

export const JobArchitectureContentLanguageProvider: FunctionComponent<JobArchitectureContentLanguageProviderProps> = ({
    children,
    initialContentLanguage,
}) => {
    const {
        applicationState: { language: initialLanguage },
    } = useApplicationContext();
    const [contentLanguage, setContentLanguage] = useState<LanguageCode>(
        initialContentLanguage ?? initialLanguage.code,
    );

    useEffect(() => {
        if (initialContentLanguage !== undefined) {
            setContentLanguage(initialContentLanguage);
        }
    }, [initialContentLanguage]);

    const value = useMemo(
        () => ({
            contentLanguage,
            setContentLanguage,
        }),
        [contentLanguage],
    );

    return (
        <JobArchitectureContentLanguageContext.Provider value={value}>
            {children}
        </JobArchitectureContentLanguageContext.Provider>
    );
};

export const useJobArchitectureContentLanguageState = (): JobArchitectureContentLanguageContextValue => {
    const context = useContext(JobArchitectureContentLanguageContext);

    if (context === null) {
        throw new Error(
            'useJobArchitectureContentLanguageState must be used within a JobArchitectureContentLanguageProvider',
        );
    }

    return context;
};

export const useJobArchitectureContentLanguage = (): LanguageCode => {
    const context = useContext(JobArchitectureContentLanguageContext);
    const {
        applicationState: { language },
    } = useApplicationContext();

    return context?.contentLanguage ?? language.code;
};
